import React from 'react';
import { TreeItem, TreeView } from '@mui/x-tree-view';
import { Business, ChevronRight, ExpandMore, KeyboardArrowDown } from '@mui/icons-material';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrganizationalUnitsTreeName } from './const';
import useData from './hooks';
import { OrganizationalUnitsMenuProps } from './types';
import { OrganizationalUnitViewer } from '90.quickConnect.Models/models/user/organizationalUnitViewer';
import { UserButtonAction } from '10.quickConnect.app/components/shared/LayOut/TopBar/RightItems/style';
import { useWindowSize } from '80.quickConnect.Core/hooks';

const OrganizationalUnitsMenu: React.FC<OrganizationalUnitsMenuProps> = ({
  isOuLocked,
}: OrganizationalUnitsMenuProps) => {
  const {
    ouMenuOpen,
    isMultiOUUser,
    ouRoots,
    currentOu,
    expandedCodes,
    isCurrentOu,
    handleOuMenuClose,
    handleOuMenuClick,
    handleOuMenuMoveTo,
  } = useData();

  const { t } = useTranslation('userSetting');
  const { t: tDeclaration } = useTranslation('declaration');
  const theme = useTheme();
  const { width } = useWindowSize();

  const renderOrganizationalUnitTree = (nodes: OrganizationalUnitViewer[]) =>
    nodes.map((node) => (
      <TreeItem
        key={node.code}
        nodeId={node.code}
        label={
          <MenuItem
            data-cy={`${OrganizationalUnitsTreeName}-ou-item`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleOuMenuMoveTo(node.code);
            }}
            selected={isCurrentOu(node.code)}
            sx={{ textDecoration: isCurrentOu(node.code) ? 'underline' : 'none' }}
          >
            <Typography variant="inherit">{node.name}</Typography>
          </MenuItem>
        }
      >
        {Array.isArray(node.children) ? renderOrganizationalUnitTree(node.children) : null}
      </TreeItem>
    ));

  return isMultiOUUser ? (
    <Box>
      <UserButtonAction
        variant="text"
        color="lightThemeTextColor"
        disabled={isOuLocked}
        data-cy={`${OrganizationalUnitsTreeName}-ou-btn`}
        id="fade-ou-button"
        aria-controls={ouMenuOpen ? 'ou-menu' : undefined}
        aria-haspopup="dialog"
        aria-expanded={ouMenuOpen ? 'true' : undefined}
        onClick={handleOuMenuClick}
        endIcon={<KeyboardArrowDown />}
        sx={{ marginRight: '0.5vw' }}
        startIcon={width && width < theme.breakpoints.values.md && <Business />}
      >
        {width && width > theme.breakpoints.values.md && currentOu?.name}
      </UserButtonAction>
      <Dialog
        id="ou-menu"
        data-cy={`${OrganizationalUnitsTreeName}-ou-dialog`}
        open={ouMenuOpen}
        onClose={() => handleOuMenuClose()}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>{t('qcapp_preference_user_organisation')}</DialogTitle>
        <DialogContent dividers>
          <TreeView
            data-cy={`${OrganizationalUnitsTreeName}-ou-tree`}
            id="ou-tree"
            defaultCollapseIcon={<ExpandMore data-cy={`${OrganizationalUnitsTreeName}-ou-toggle`} />}
            defaultExpandIcon={<ChevronRight data-cy={`${OrganizationalUnitsTreeName}-ou-toggle`} />}
            defaultExpanded={expandedCodes}
          >
            {renderOrganizationalUnitTree(ouRoots)}
          </TreeView>
        </DialogContent>
        <DialogActions>
          <Button data-cy={`${OrganizationalUnitsTreeName}-ou-cancel`} onClick={() => handleOuMenuClose()}>
            {tDeclaration('qcapp_dialog_action_cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  ) : null;
};

export default observer(OrganizationalUnitsMenu);
